import { useCallback, useMemo, useState } from "react";
import KaboodleModal from "./KaboodleModal";
import Card from "components/Card/Card";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useSelector } from "utils/redux";
import Button from "components/Button/Button";
import { useUploadClaimsImportForFileMutation } from "services/file-api";
import { useDropzone } from "react-dropzone";
import "./UploadClaimAndQueryModal.scss";

type UploadClaimsOnFileModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

const UploadClaimsOnFileModal: React.FC<UploadClaimsOnFileModalProps> = ({
  onClose,
  isOpen,
}: UploadClaimsOnFileModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadClaimsForFile, { isLoading }] =
    useUploadClaimsImportForFileMutation();
  const currentFile = useSelector((state) => state.files.currentFile);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setUploadError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const { reset, handleSubmit } = useForm();

  //   const { constants } = useConstants();
  //   const importTypes = constants?.IMPORT_TYPES || [];

  const footerContent = useMemo(() => {
    const onSubmit: SubmitHandler<FormData> = async () => {
      const callData = new FormData();
      callData.append("file", file);
      await uploadClaimsForFile({
        id: currentFile?.id,
        file: callData,
      })
        .unwrap()
        .then(() => {
          reset();
          onClose();
          setFile(null);
        })
        .catch((error) =>
          setUploadError(error?.data?.error || "An error occurred"),
        );
    };

    return (
      <div>
        <div className="upload-button">
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={!file || isLoading}
          >
            {uploadError ? "Try Again" : isLoading ? "Uploading..." : "Upload"}
          </Button>
        </div>
        {uploadError && (
          <div className="error-footer">
            <p>{uploadError}</p>
            <Button onClick={() => setUploadError(null)}>Clear</Button>
          </div>
        )}
      </div>
    );
  }, [
    currentFile?.id,
    file,
    handleSubmit,
    isLoading,
    onClose,
    reset,
    uploadError,
    uploadClaimsForFile,
  ]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
    setFile(null);
  }, [onClose, reset]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Upload RRE Claims">
        <div className="claim-upload-file">
          <label htmlFor="name">File</label>
          <div
            {...getRootProps()}
            className={`dropzone ${isDragActive ? "active" : "active"}`}
          >
            <input {...getInputProps()} />
            {file ? (
              <p>
                <b>Selected file:</b> {file.name}
              </p>
            ) : (
              <p>
                <b>Drag & Drop</b> or <b>Click</b> to Select File
              </p>
            )}
          </div>
        </div>
      </Card>
    </KaboodleModal>
  );
};

export default UploadClaimsOnFileModal;
